import { matchIsValidTel } from 'mui-tel-input';
import { useState } from 'react';

import { ZERO } from '@/constants';
import { useCurrentUser } from '@/hooks';
import { type User, type UserNotifications } from '@/types';
import { Button, Dialog, DialogActions, DialogContent, Divider } from '@/ui';

import Details from './Details';
import Hours from './Hours';
import Notifications from './Notifications';

import type { FC } from 'react';

import {
  useUpdateUserNotificationsMutation,
  useUpdateUserProfileMutation,
} from '@/store/api/userApi';

interface EditAccountProps {
  coach: User;
  onClose: (updated?: Partial<User>) => void;
}

const EditCoachAccount: FC<EditAccountProps> = ({ coach, onClose }) => {
  const { refetchUser } = useCurrentUser();
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const [updateUserNotifications] = useUpdateUserNotificationsMutation();
  const [isSaving, setIsSaving] = useState(false);
  const [timezone, setTimezone] = useState(coach.profile?.timezone || '');
  const [phoneValue, setPhoneValue] = useState(coach.profile?.phone || '');
  const [phoneE164, setPhoneE164] = useState(coach.profile?.phone || '');
  const [phoneNationalNumber, setPhoneNationalNumber] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [preferences, setPreferences] = useState(coach.userNotifications ?? {});
  const onPhoneChange = (
    value: string,
    e164: string,
    nationalNumber: string,
    reason: 'country' | 'input',
  ) => {
    if (reason === 'input' || nationalNumber.trim().length > ZERO) {
      setPhoneTouched(true);
    }
    setPhoneValue(value);
    setPhoneE164(e164);
    setPhoneNationalNumber(nationalNumber);
  };
  const onTimezoneChange = (value: string) => setTimezone(value);

  const hasPhone =
    phoneNationalNumber.trim().length > ZERO ||
    (!phoneTouched && phoneValue.trim().length > ZERO);
  const isPhoneInvalid =
    hasPhone &&
    (!matchIsValidTel(phoneValue) ||
      (phoneTouched && !phoneE164.startsWith('+')));
  const phoneHelperText = isPhoneInvalid
    ? 'Phone is invalid'
    : '*Required to use SMS notifications';

  const onSave = async () => {
    try {
      setIsSaving(true);
      await updateUserProfile({
        id: coach.id,
        userProfile: {
          timezone,
          ...(phoneTouched ? { phone: hasPhone ? phoneE164 : '' } : {}),
        },
      });
      await updateUserNotifications({
        id: coach.id,
        notifications: preferences,
      });
      await refetchUser();
      setIsSaving(false);
      onClose();
    } catch (error) {
      console.error('Error saving coach profile', error);
    } finally {
      setIsSaving(false);
    }
  };

  const onPreferencesChange = (value: UserNotifications) =>
    setPreferences(value);

  const onCancel = () => onClose();

  const disableSave = phoneTouched && isPhoneInvalid;

  return (
    <Dialog maxWidth="md" fullWidth open>
      <DialogContent>
        <Details
          capacity={coach.coachProfile?.capacity ?? ZERO}
          email={coach.email}
          phone={phoneValue}
          phoneHelperText={phoneHelperText}
          phoneInvalid={isPhoneInvalid}
          timezone={timezone}
          onPhoneChange={onPhoneChange}
          onTimezoneChange={onTimezoneChange}
        />
        <Divider className="my-8" />
        <Hours />
        <Divider className="my-8" />
        <Notifications
          preferences={preferences}
          onPreferencesChange={onPreferencesChange}
        />
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={disableSave}
          loading={isSaving}
          variant="contained"
          onClick={onSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCoachAccount;
